import { Edge, Node } from 'reactflow';
import { NodesTypes, parentNodeTypes } from '@constants/canvas/general';
import {
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  matchesModelFiltersDto,
  ModelFilters,
} from '@context/ProjectFilters/matchesModelFilters';
import {
  ICanvasNodesResponse,
  NodeDTOTypes,
} from '@store/services/nodes/types';
import {
  checkScopeFilter,
  SCOPE_FILTER_NO_OPERATIONALISED,
} from '@utils/domains';

type OtherModeNode = ICanvasNodesResponse['nodes'][number];

export const hideNodesOutsideFocus = (
  nodes: Node[],
  isFocusActive: boolean,
  focusedIds: string[],
): Node[] => {
  if (!isFocusActive) return nodes;

  const focusedSet = new Set(focusedIds);

  return nodes.map((node) =>
    node.type === NodesTypes.CustomNode && !focusedSet.has(node.id)
      ? { ...node, hidden: true }
      : node,
  );
};

export const unselectNodes = (nodes: Node[]): Node[] =>
  nodes.map((node) => (node.selected ? { ...node, selected: false } : node));

export const unselectEdges = (edges: Edge[]): Edge[] =>
  edges.map((edge) =>
    edge.selected ? { ...edge, selected: false, animated: false } : edge,
  );

export const getVisibleNodeIds = (nodes: Node[]): Set<string> =>
  new Set(nodes.filter(({ hidden }) => !hidden).map(({ id }) => id));

export const getExportEntityIds = (nodes: Node[]): string[] =>
  nodes.reduce<string[]>((acc, { id, hidden, type }) => {
    if (!hidden && type === NodesTypes.CustomNode) acc.push(id);

    return acc;
  }, []);

const NoOperationalisedAncestorTypes = new Set<NodeDTOTypes>([
  NodeDTOTypes.SecurityAttribute,
  NodeDTOTypes.BusinessAttribute,
  NodeDTOTypes.StrategicEnabler,
  NodeDTOTypes.StrategicPriority,
]);

const collectNoOperationalisedAncestors = (nodes: OtherModeNode[]) => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const ancestorIds = new Set<string>();
  const visited = new Set<string>();

  const traverseParents = (nodeId: string) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    nodesById.get(nodeId)?.parentsIds?.forEach((parentId) => {
      const parent = nodesById.get(parentId);

      if (!parent || !NoOperationalisedAncestorTypes.has(parent.type)) return;

      ancestorIds.add(parentId);
      traverseParents(parentId);
    });
  };

  nodes.forEach((node) => {
    if (
      !checkScopeFilter(
        [SCOPE_FILTER_NO_OPERATIONALISED],
        node.type,
        node.domainNodes,
      )
    ) {
      return;
    }

    traverseParents(node.id);
  });

  return ancestorIds;
};

const getSubLayer = (node: OtherModeNode) =>
  parentNodeTypes[node.type as keyof typeof parentNodeTypes];

const BaConnectivitySubLayers: SubLayerTypes[] = [
  ContextualSubLayerTypes.StrategicPriorities,
  ContextualSubLayerTypes.StrategicEnablers,
  ConceptualSubLayerTypes.BusinessAttributes,
  ConceptualSubLayerTypes.SecurityAttributes,
];

const collectAncestorsOfMatchedServices = (
  nodes: OtherModeNode[],
  isServiceIncluded: (node: OtherModeNode) => boolean,
) => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const ancestorIds = new Set<string>();

  const traverseParents = (nodeId: string) => {
    const node = nodesById.get(nodeId);

    if (!node || ancestorIds.has(nodeId)) return;

    ancestorIds.add(nodeId);
    node.parentsIds?.forEach(traverseParents);
  };

  nodes.forEach((node) => {
    if (node.type !== NodeDTOTypes.Service || !isServiceIncluded(node)) return;

    node.parentsIds?.forEach(traverseParents);
  });

  return ancestorIds;
};

export const getOtherModeEntityIds = ({
  nodes,
  currentModeSubLayers,
  domains,
  modelFilters,
}: {
  nodes: OtherModeNode[];
  currentModeSubLayers: Set<string>;
  domains: string[];
  modelFilters: ModelFilters;
}): string[] => {
  const noOperationalisedAncestorIds = domains.includes(
    SCOPE_FILTER_NO_OPERATIONALISED,
  )
    ? collectNoOperationalisedAncestors(nodes)
    : new Set<string>();

  const matchesFilters = (node: OtherModeNode) => {
    const isInScope =
      checkScopeFilter(domains, node.type, node.domainNodes) ||
      noOperationalisedAncestorIds.has(node.id);

    return (
      isInScope && matchesModelFiltersDto(node, getSubLayer(node), modelFilters)
    );
  };

  const connectedAncestorIds = modelFilters.operationalStatuses.length
    ? collectAncestorsOfMatchedServices(nodes, matchesFilters)
    : null;

  return nodes.reduce<string[]>((acc, node) => {
    const subLayer = getSubLayer(node);

    if (!subLayer || currentModeSubLayers.has(subLayer)) return acc;

    const isIncluded =
      connectedAncestorIds && BaConnectivitySubLayers.includes(subLayer)
        ? connectedAncestorIds.has(node.id)
        : matchesFilters(node);

    if (isIncluded) acc.push(node.id);

    return acc;
  }, []);
};
